import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useUniverseTheme } from '../../contexts/UniverseThemeContext';
import universeService from '../../services/universeService';
import styles from './UniverseSelector.module.css';

const DROPDOWN_GAP_PX = 8;

const UniverseSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [universes, setUniverses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const { currentUniverse, setUniverseTheme } = useUniverseTheme();
  const navigate = useNavigate();
  const selectorRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchUniverses = async () => {
      try {
        const data = await universeService.getUniverses();
        setUniverses(data);
      } catch (err) {
        console.error('Erreur lors du chargement des univers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUniverses();
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) {
      return;
    }

    const rect = buttonRef.current.getBoundingClientRect();
    setDropdownStyle({
      top: `${rect.bottom + DROPDOWN_GAP_PX}px`,
      left: `${rect.left}px`,
      minWidth: `${Math.max(rect.width, 280)}px`,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updateDropdownPosition();

    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideSelector = selectorRef.current?.contains(event.target);
      const clickedInsideDropdown = dropdownRef.current?.contains(event.target);

      if (!clickedInsideSelector && !clickedInsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentUniverseData = universes.find((u) => u.slug === currentUniverse) || null;

  const handleUniverseSelect = (universe) => {
    setUniverseTheme(universe.slug);
    setIsOpen(false);
    navigate(`/univers/${universe.slug}`);
  };

  const handlePortalClick = () => {
    setUniverseTheme('portal');
    setIsOpen(false);
    navigate('/');
  };

  if (loading || universes.length === 0) {
    return null;
  }

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      className={styles.dropdown}
      style={dropdownStyle}
      role="menu"
    >
      <button
        type="button"
        className={`${styles.dropdownItem} ${
          currentUniverse === 'portal' ? styles.active : ''
        }`}
        onClick={handlePortalClick}
      >
        <span className={styles.itemName}>Portail</span>
        <span className={styles.itemDescription}>Vue d'ensemble</span>
      </button>
      {universes.map((universe) => (
        <button
          type="button"
          key={universe.id}
          className={`${styles.dropdownItem} ${
            currentUniverse === universe.slug ? styles.active : ''
          }`}
          onClick={() => handleUniverseSelect(universe)}
        >
          <span className={styles.itemName}>{universe.name}</span>
          {universe.description && (
            <span className={styles.itemDescription}>
              {universe.description.length > 50
                ? `${universe.description.substring(0, 50)}...`
                : universe.description}
            </span>
          )}
          {universe.forumCount !== undefined && (
            <span className={styles.itemStats}>
              {universe.forumCount} forums
            </span>
          )}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <>
      <div className={styles.selector} ref={selectorRef}>
        <button
          ref={buttonRef}
          type="button"
          className={styles.selectorButton}
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <span className={styles.selectorValue}>
            {currentUniverseData?.name || 'Portail'}
          </span>
          <svg
            className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
      {dropdown && createPortal(dropdown, document.body)}
    </>
  );
};

export default UniverseSelector;
